/**
 * The console's own help text (the design's CONSOLE_INDEX and CONSOLE_FAQ) and the
 * searches the command palette and the Help drawer run over it, ported from
 * Aithora ConsoleV3.dc.html (paletteVals L28743, hx* L33462-33862).
 *
 * Pure data and functions: the console loads the text (apps/console/src/data) and
 * passes it in, so a new copy of the text is a one-line swap there.
 */
import { canSee, type Role, type ViewId } from './roles';

// ── The data files' shapes ──────────────────────────────────────────────────
/** A provenance state or a badge name: LIVE, SAMPLE, MODEL, STATIC, ... */
export type IndexState = string;

export interface ConsoleIndexEntry {
  /** Group ("Running a building"). */
  g: string;
  /** Title ("Overview"). */
  t: string;
  state?: IndexState;
  reviewed?: string;
  see?: string[];
  purpose: string;
  /** Design role names ("Operator", "Facility Admin", "Everyone"). */
  roles?: string[];
  controls?: [string, string][];
  truth?: [string, string][];
  howTo?: string[];
  gotcha?: string[];
  whenNot?: string[];
}

export interface ConsoleFaq {
  lead?: string;
  groups: { g: string; questions: { q: string; a: string; roles?: string[] }[] }[];
  closing?: string;
}

// ── Roles and views, design names to ours ───────────────────────────────────
/** The design's name for a stored role, as the help text's `roles` lists use it. */
export function helpRoleName(role: Role | null): string {
  switch (role) {
    case 'SUPER_ADMIN':
    case 'OPERATOR':
    case 'PARTNER':
      return 'Operator';
    case 'ADMIN':
      return 'Facility Admin';
    case 'CAREGIVER':
    case 'STAFF':
      return 'Care Staff';
    case 'SALES':
      return 'Sales';
    case 'DEVELOPER':
      return 'Dev';
    case 'FAMILY_MEMBER':
      return 'Family';
    case 'RESIDENT':
      return 'Resident';
    case 'MEMBER':
      return 'Aithora Home';
    default:
      return 'Operator';
  }
}

/** The design's view ids (help links, guide links) to the console's. */
const DESIGN_VIEW: Record<string, ViewId> = {
  overview: 'overview', home: 'home', companion: 'companion', residents: 'residents', guardian: 'guardian',
  worklist: 'worklist', family: 'family', familyPortal: 'family', monitoring: 'oversight', oversight: 'oversight',
  atlas: 'atlas', policy: 'policy', finance: 'finance', capture: 'capture', scorecard: 'performance',
  benchmark: 'performance', performance: 'performance', atlasIntel: 'atlasIntel', ai: 'atlasIntel', sales: 'sales',
  crm: 'sales', pulse: 'pulse', settings: 'settings', help: 'help',
};
export function viewFromDesign(v: string | null | undefined): ViewId | null {
  return v ? DESIGN_VIEW[v] ?? null : null;
}

/** Is this entry (or question) written for the role? (design: mine()) */
export function forRole(roles: readonly string[] | undefined, roleName: string): boolean {
  return !roles || roles.includes('Everyone') || roles.includes(roleName);
}

// ── Command palette (paletteVals L28743) ────────────────────────────────────
/** Page title in the help text to the console page it opens (design NAVMAP). */
const NAVMAP: Record<string, ViewId> = {
  Overview: 'overview', Residents: 'residents', Guardian: 'guardian', Worklist: 'worklist', 'Sales Kit': 'sales', CRM: 'sales',
  Scorecard: 'performance', Performance: 'performance', Benchmark: 'performance', 'Atlas Intelligence': 'atlasIntel',
  Finance: 'finance', Policy: 'policy', 'Policy Controls': 'policy', Settings: 'settings', Pulse: 'pulse', 'System Pulse': 'pulse',
  Oversight: 'oversight', 'Monitoring and the Truth Ledger': 'oversight',
  Capture: 'capture', Atlas: 'atlas', 'Family Portal': 'family', 'Aithora Home': 'home', Wearables: 'settings',
};
/** Sales Kit and CRM share a page, so the tab is carried (design NAVTAB). */
const NAVTAB: Record<string, string> = { 'Sales Kit': 'Sales Kit', CRM: 'CRM' };

/** The palette's provenance chip colours (design BADGE). */
export const PALETTE_BADGE: Record<string, [string, string]> = {
  LIVE: ['#1F6B52', 'rgba(47,138,111,.16)'],
  SAMPLE: ['#54606C', 'rgba(34,39,46,.18)'],
  MODEL: ['#2F5C97', 'rgba(79,138,214,.16)'],
  STATIC: ['#54606C', 'rgba(34,39,46,.18)'],
  EMPTY: ['#54606C', 'rgba(34,39,46,.08)'],
  // a safety limit reads red, because it is a boundary rather than a provenance note
  LIMIT: ['#B3261E', 'rgba(179,38,30,.13)'],
};
export function paletteBadge(k: string | undefined): [string, string] {
  return (k && PALETTE_BADGE[k]) || PALETTE_BADGE.EMPTY;
}

export interface PaletteGoTo {
  label: string;
  sub: string;
  badge: string;
  view: ViewId;
  tab: string | null;
}
export interface PaletteWhatIs {
  label: string;
  body: string;
  from: string;
  badge: string;
}

/**
 * "Go to" (navigation) and "What is" (a definition read in place) for a query. Only
 * entries written for the role are searched, and only pages the role may open are
 * offered, so the palette is never a way round the side nav.
 */
export function paletteSearch(index: readonly ConsoleIndexEntry[], query: string, role: Role | null): { goTo: PaletteGoTo[]; whatIs: PaletteWhatIs[] } {
  const q = query.trim().toLowerCase();
  const goTo: PaletteGoTo[] = [];
  const whatIs: PaletteWhatIs[] = [];
  if (!q) return { goTo, whatIs };
  const roleName = helpRoleName(role);
  for (const p of index) {
    if (!forRole(p.roles, roleName)) continue;
    const view = NAVMAP[p.t];
    if (p.t.toLowerCase().includes(q) && view && canSee(role, view)) {
      goTo.push({ label: p.t, sub: p.g, badge: p.state ?? '', view, tab: NAVTAB[p.t] ?? null });
    }
    for (const c of p.controls ?? []) {
      if (`${c[0]} ${c[1]}`.toLowerCase().includes(q)) whatIs.push({ label: c[0], body: c[1], from: p.t, badge: p.state ?? '' });
    }
    for (const c of p.truth ?? []) {
      if (`${c[0]} ${c[1]}`.toLowerCase().includes(q)) whatIs.push({ label: `${c[0]} on ${p.t}`, body: c[1], from: p.t, badge: c[0] });
    }
    // Safety limits are the highest-stakes strings in the index: reachable from the fastest search.
    for (const s of p.whenNot ?? []) {
      if (s.toLowerCase().includes(q)) whatIs.push({ label: `Not for: ${p.t}`, body: s, from: p.t, badge: 'LIMIT' });
    }
  }
  return { goTo: goTo.slice(0, 5), whatIs: whatIs.slice(0, 6) };
}

// ── Help assistant (hx*, L33462) ────────────────────────────────────────────
/** The help text's title for a page (design hxEntryFor's T). */
const TITLE_FOR_VIEW: Partial<Record<ViewId, readonly string[]>> = {
  overview: ['Overview'], residents: ['Residents'], guardian: ['Guardian'], worklist: ['Worklist'], sales: ['Sales Kit'],
  performance: ['Performance', 'Scorecard'], atlasIntel: ['Atlas Intelligence'], finance: ['Finance'], policy: ['Policy Controls'],
  oversight: ['Monitoring and the Truth Ledger'], settings: ['Settings'], pulse: ['Pulse'],
  capture: ['Capture'], atlas: ['Atlas'], companion: ['Atlas'], family: ['Family Portal'], home: ['Aithora Home'],
};
export function helpEntryFor(index: readonly ConsoleIndexEntry[], view: ViewId | null): ConsoleIndexEntry | null {
  const titles = view ? TITLE_FOR_VIEW[view] : undefined;
  if (!titles) return null;
  for (const t of titles) {
    const e = index.find((x) => x.t === t);
    if (e) return e;
  }
  return null;
}

/** The page a help title opens (design hxViewFor). */
const VIEW_FOR_TITLE: Record<string, ViewId> = {
  Overview: 'overview', Residents: 'residents', Guardian: 'guardian', Worklist: 'worklist', 'Sales Kit': 'sales',
  Scorecard: 'performance', Performance: 'performance', 'Atlas Intelligence': 'atlasIntel', Finance: 'finance',
  'Policy Controls': 'policy', 'Monitoring and the Truth Ledger': 'oversight', Settings: 'settings', Pulse: 'pulse',
  Capture: 'capture', Atlas: 'atlas', 'Family Portal': 'family', 'Aithora Home': 'home', Wearables: 'settings',
};
export function helpViewFor(title: string): ViewId | null {
  return VIEW_FOR_TITLE[title] ?? null;
}

export interface HelpPage {
  view: ViewId;
  name: string;
  words: string;
}
/** Every page the role may open, with the words people type for it (design hxPages). */
export function helpPages(role: Role | null): HelpPage[] {
  const P: [ViewId, string, string][] = [
    ['overview', 'Overview', 'overview dashboard home daily'], ['home', 'Aithora Home', 'aithora home member'],
    ['companion', 'Atlas', 'companion voice conversation'], ['residents', 'Residents', 'residents resident roster chart'],
    ['guardian', 'Guardian', 'guardian safety signals referral aps'], ['worklist', 'Worklist', 'worklist shift tasks med pass'],
    ['family', 'Family Portal', 'family portal relatives'], ['oversight', 'Oversight', 'oversight monitoring truth ledger ledger audit decisions'],
    ['atlas', 'Atlas', 'atlas'], ['policy', 'Policy Controls', 'policy policies guards controls threshold'],
    ['finance', 'Finance', 'finance treasury billing receivables cash'], ['performance', 'Performance', 'performance scorecard benchmark efficiency rebates reimbursement portfolio trajectory'],
    ['atlasIntel', 'Atlas Intelligence', 'intelligence underwriting sourcing acquisition'], ['sales', 'Sales', 'sales kit crm deals pitch'],
    ['pulse', 'System Pulse', 'system pulse uptime status incident'], ['settings', 'Settings', 'settings organization access integrations retention users'],
  ];
  return P.filter(([v]) => canSee(role, v)).map(([view, name, words]) => ({ view, name, words }));
}

/** Pages named in a piece of text, best first (design hxMatchPages). */
export function matchPages(role: Role | null, text: string, max = 3, namesOnly = false): HelpPage[] {
  const t = ` ${String(text || '').toLowerCase().replace(/[^a-z0-9 ]/g, ' ')} `;
  const out: { p: HelpPage; s: number }[] = [];
  for (const p of helpPages(role)) {
    let hit = t.includes(` ${p.name.toLowerCase()} `) ? 10 : 0;
    if (!namesOnly) p.words.split(' ').forEach((w) => {
      if (w.length > 2 && t.includes(` ${w} `)) hit += 1;
    });
    if (hit) out.push({ p, s: hit });
  }
  return out.sort((a, b) => b.s - a.s).slice(0, max).map((x) => x.p);
}

export type HelpDoc =
  | { kind: 'page'; title: string; text: string; e: ConsoleIndexEntry }
  | { kind: 'faq'; title: string; text: string; a: string; g: string };

/** Every page entry and FAQ written for the role (design hxDocs). */
export function helpDocs(index: readonly ConsoleIndexEntry[], faq: ConsoleFaq, role: Role | null): HelpDoc[] {
  const roleName = helpRoleName(role);
  const flat = (a: readonly (string | readonly string[])[] | undefined) => (a ?? []).map((x) => (Array.isArray(x) ? x.join(': ') : x)).join(' ');
  const docs: HelpDoc[] = [];
  index.filter((e) => forRole(e.roles, roleName)).forEach((e) =>
    docs.push({ kind: 'page', title: e.t, text: [e.purpose, flat(e.controls), flat(e.truth), flat(e.howTo), flat(e.gotcha), flat(e.whenNot)].join(' '), e }),
  );
  faq.groups.forEach((g) => g.questions.filter((x) => forRole(x.roles, roleName)).forEach((x) => docs.push({ kind: 'faq', title: x.q, text: `${x.q} ${x.a}`, a: x.a, g: g.g })));
  return docs;
}

const STOP = new Set(['the', 'and', 'for', 'what', 'why', 'how', 'does', 'this', 'that', 'with', 'are', 'can', 'you', 'from', 'page', 'mean', 'means', 'there', 'when', 'into', 'have', 'not']);

/** The best three help entries for a question, on the page `view` (design hxRank). */
export function rankHelp(index: readonly ConsoleIndexEntry[], faq: ConsoleFaq, role: Role | null, q: string, view: ViewId | null): HelpDoc[] {
  const words = String(q).toLowerCase().replace(/[^a-z0-9 -]/g, ' ').split(/\s+/).filter((w) => w.length > 2 && !STOP.has(w));
  const here = helpEntryFor(index, view);
  const named = matchPages(role, q, 2).map((p) => helpEntryFor(index, p.view)?.t ?? null).filter((t): t is string => !!t);
  return helpDocs(index, faq, role)
    .map((d) => {
      const t = d.title.toLowerCase();
      const b = d.text.toLowerCase();
      let sc = 0;
      words.forEach((w) => {
        if (t.includes(w)) sc += 3;
        if (b.includes(w)) sc += 1;
      });
      if (sc > 0 && here && d.kind === 'page' && d.title === here.t) sc += 1;
      if (d.kind === 'page' && named.includes(d.title)) sc += 6;
      return { d, s: sc };
    })
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s)
    .filter((x, i, a) => i === 0 || x.s >= a[0].s * 0.6)
    .slice(0, 3)
    .map((x) => x.d);
}

/** The answer stitched from the help text itself, when no written answer is available (design hxStitch). */
export function stitchHelp(hits: readonly HelpDoc[]): string[] {
  const d = hits[0];
  if (!d) return [];
  if (d.kind === 'faq') return [d.a];
  const e = d.e;
  const out = [e.purpose];
  if (e.gotcha && e.gotcha.length) out.push(`Easy to get wrong: ${e.gotcha[0]}`);
  else if (e.howTo && e.howTo.length) out.push(e.howTo[0]);
  return out;
}

/** A hit as the drawer shows it under an answer (title and whether it is a page or an FAQ). */
export interface HelpHit {
  kind: 'page' | 'faq';
  title: string;
  g?: string;
}
export interface HelpPreset {
  paras: string[];
  hits: HelpHit[];
}
export interface HelpQuestion {
  label: string;
  g?: string;
  preset?: HelpPreset;
}

/** The questions one entry answers from its own text (design hxQs). */
export function questionsFor(e: ConsoleIndexEntry | null): HelpQuestion[] {
  if (!e) return [];
  const n = e.t;
  const hit: HelpHit[] = [{ kind: 'page', title: n }];
  const out: HelpQuestion[] = [];
  // Concept entries ("Why numbers carry badges") are not pages: ask them as themselves.
  if (!helpViewFor(n)) {
    out.push({ label: n, preset: { paras: [e.purpose], hits: hit } });
    (e.controls ?? []).slice(0, 5).forEach((c) => out.push({ label: `What is “${c[0]}”?`, preset: { paras: [c[1]], hits: hit } }));
    if (e.gotcha && e.gotcha.length) out.push({ label: `Where people go wrong: ${n}`, preset: { paras: e.gotcha.slice(0, 2), hits: hit } });
    return out;
  }
  out.push({ label: `What is ${n} for?`, preset: { paras: [e.purpose], hits: hit } });
  if (e.gotcha && e.gotcha.length) out.push({ label: `What is easy to get wrong on ${n}?`, preset: { paras: e.gotcha.slice(0, 2), hits: hit } });
  if (e.howTo && e.howTo.length) out.push({ label: `How do I use ${n}?`, preset: { paras: e.howTo.slice(0, 3), hits: hit } });
  if (e.truth && e.truth.length) out.push({ label: `What on ${n} is real, and what is sample?`, preset: { paras: e.truth.map((x) => `${x[0]}: ${x[1]}`), hits: hit } });
  (e.controls ?? []).slice(0, 4).forEach((c) => out.push({ label: `What does “${c[0]}” mean on ${n}?`, preset: { paras: [c[1]], hits: hit } }));
  if (e.whenNot && e.whenNot.length) out.push({ label: `When should I not rely on ${n}?`, preset: { paras: e.whenNot.slice(0, 2), hits: hit } });
  return out;
}

/** Every question the help text answers for the role (design hxBank). */
export function questionBank(index: readonly ConsoleIndexEntry[], faq: ConsoleFaq, role: Role | null): HelpQuestion[] {
  const roleName = helpRoleName(role);
  const out: HelpQuestion[] = [];
  index.filter((e) => forRole(e.roles, roleName)).forEach((e) => questionsFor(e).forEach((q) => out.push(q)));
  faq.groups.forEach((g) =>
    g.questions.filter((x) => forRole(x.roles, roleName)).forEach((x) => out.push({ label: x.q, g: g.g, preset: { paras: [x.a], hits: [{ kind: 'faq', title: x.q, g: g.g }] } })),
  );
  return out;
}

/**
 * The three suggestions the drawer opens with (design hxVals' sug): the tab you are on,
 * what the page is for, what is easy to get wrong, then FAQs to fill.
 */
export function helpSuggestions(index: readonly ConsoleIndexEntry[], faq: ConsoleFaq, role: Role | null, view: ViewId | null, tabLabel: string | null): HelpQuestion[] {
  const here = helpEntryFor(index, view);
  const pageName = here ? here.t : null;
  const roleName = helpRoleName(role);
  const faq1: { q: string; a: string }[] = [];
  faq.groups.forEach((g) => g.questions.forEach((x) => {
    if (forRole(x.roles, roleName)) faq1.push(x);
  }));
  const sug: HelpQuestion[] = [];
  if (here && pageName && tabLabel && pageName.toLowerCase().indexOf(tabLabel.toLowerCase()) !== 0) {
    const tl = tabLabel.toLowerCase();
    const c = (here.controls ?? []).find((x) => x[0].toLowerCase().includes(tl) || tl.includes(x[0].toLowerCase()));
    sug.push({ label: `What is the ${tabLabel} tab on ${pageName} for?`, preset: c ? { paras: [c[1]], hits: [{ kind: 'page', title: here.t }] } : undefined });
  }
  if (here && pageName) {
    sug.push({ label: `What is ${pageName} for?`, preset: { paras: [here.purpose], hits: [{ kind: 'page', title: here.t }] } });
    if (here.gotcha && here.gotcha.length) sug.push({ label: `What is easy to get wrong on ${pageName}?`, preset: { paras: here.gotcha.slice(0, 2), hits: [{ kind: 'page', title: here.t }] } });
    else if (here.howTo && here.howTo.length) sug.push({ label: `How do I use ${pageName}?`, preset: { paras: here.howTo.slice(0, 3), hits: [{ kind: 'page', title: here.t }] } });
  }
  faq1.slice(0, Math.max(0, 3 - sug.length)).forEach((x) => sug.push({ label: x.q, preset: { paras: [x.a], hits: [{ kind: 'faq', title: x.q }] } }));
  return sug.slice(0, 3);
}

/** Questions from the bank that contain every typed word (design hxAuto). */
export function autocompleteQuestions(bank: readonly HelpQuestion[], input: string): HelpQuestion[] {
  const iw = String(input || '').toLowerCase().split(/\s+/).filter((w) => w.length > 1);
  if (!iw.length || String(input).trim().length < 3) return [];
  return bank.filter((b) => {
    const l = b.label.toLowerCase();
    return iw.every((w) => l.includes(w));
  }).slice(0, 4);
}
